import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/app-layout";
import { LoginPage } from "@/pages/login";
import { RegisterPage } from "@/pages/register";
import { DashboardPage } from "@/pages/dashboard";
import { VenuesPage } from "@/pages/venues";
import { VenueDetailPage } from "@/pages/venue-detail";
import { MenusPage } from "@/pages/menus";
import { MenuDetailPage } from "@/pages/menu-detail";
import { ThemePage } from "@/pages/theme";
import { QrCodesPage } from "@/pages/qr-codes";
import { TablesPage } from "@/pages/tables";
import { AnalyticsPage } from "@/pages/analytics";
import { OrdersPage } from "@/pages/orders";
import { AnnouncementPage } from "@/pages/announcement";
import { BannersPage } from "@/pages/banners";
import { SettingsPage } from "@/pages/settings";
import { VenueLogsPage } from "@/pages/venue-logs";
import { SalesPage } from "@/pages/sales";
import { StaffPage } from "@/pages/staff";
import { LoyaltyPage } from "@/pages/loyalty";
import { KitchenPage } from "@/pages/kitchen";
import { ReviewsPage } from "@/pages/reviews";
import { MenuSection } from "@/pages/sections/menu-section";
import { OrdersSection } from "@/pages/sections/orders-section";
import { CustomersSection } from "@/pages/sections/customers-section";
import { ReportsSection } from "@/pages/sections/reports-section";
import { YemeksepetiPage } from "@/pages/yemeksepeti";
import { TrendyolgoPage } from "@/pages/trendyolgo";
import { RouteErrorBoundary } from "@/components/error-boundary";

// Eski tek-link URL'leri yeni nested konuma yönlendiren helper. `<Navigate
// to="../X" />` default relative resolution route-hierarchy üzerinden
// çalışıyor (URL hierarchy değil) — flat route tanımında `..` AppLayout'a
// kadar çıkıp `/X`'e gidiyor, bu da 404. useParams ile absolute yola
// gönderiyoruz: edge case yok.
function VenueRedirect({ to }: { to: string }) {
  const { venueId } = useParams();
  return <Navigate to={`/venues/${venueId}/${to}`} replace />;
}

// Sidebar 13 link → 5 grup'a indirildi. Her grup bir "section" sayfası;
// içerideki tab navigation (SectionShell) ilgili child route'u render
// ediyor. Eski tek-link URL'leri React Router <Navigate> ile yeni
// nested yola yönlendiriliyor → kullanıcının eski bookmark/email
// linkleri bozulmuyor.
export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/register", element: <RegisterPage />, errorElement: <RouteErrorBoundary /> },
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "venues", element: <VenuesPage /> },

      // Venue açılınca varsayılan olarak Menü sekmesi açılır — kullanıcının
      // en sık iş yaptığı yer. Eskiden VenueDetail (=Ayarlar) açılıyordu,
      // bu yanlıştı çünkü ilk iş menü yönetimidir.
      { path: "venues/:venueId", element: <Navigate to="menu" replace /> },

      // ─── MENÜ SECTION ───
      {
        path: "venues/:venueId/menu",
        element: <MenuSection />,
        children: [
          { index: true, element: <MenusPage /> },
          { path: "themes", element: <ThemePage /> },
          { path: "announcement", element: <AnnouncementPage /> },
          { path: "banners", element: <BannersPage /> },
        ],
      },
      // Menu detayı section dışında — üstte tabs varken alt sayfaları
      // göstermek garip olur. URL: /venues/:id/menus/:menuId (eski yol)
      { path: "venues/:venueId/menus/:menuId", element: <MenuDetailPage /> },

      // ─── SİPARİŞ SECTION ───
      {
        path: "venues/:venueId/orders",
        element: <OrdersSection />,
        children: [
          { index: true, element: <TablesPage /> },
          { path: "kitchen", element: <KitchenPage /> },
          { path: "staff", element: <StaffPage /> },
          // Sipariş listesi (OrdersPage) — kitchen ile aynı veriye bakıyor,
          // şimdilik gizli route olarak burada bırakıldı.
          { path: "list", element: <OrdersPage /> },
        ],
      },

      // ─── MÜŞTERİ SECTION ───
      {
        path: "venues/:venueId/customers",
        element: <CustomersSection />,
        children: [
          { index: true, element: <ReviewsPage /> },
          { path: "loyalty", element: <LoyaltyPage /> },
        ],
      },

      // ─── RAPORLAR SECTION ───
      {
        path: "venues/:venueId/reports",
        element: <ReportsSection />,
        children: [
          { index: true, element: <AnalyticsPage /> },
          { path: "sales", element: <SalesPage /> },
          { path: "logs", element: <VenueLogsPage /> },
        ],
      },

      // İşletme ayarları — section dışında, tek başına. QR yönetimi de
      // ayarların altında: ayrı sidebar item olmaktan çıkarıldı.
      { path: "venues/:venueId/settings", element: <VenueDetailPage /> },
      { path: "venues/:venueId/qr-codes", element: <QrCodesPage /> },

      // Yemeksepeti — beta entegrasyon, sadece admin tarafından açılan
      // venue'lerde sidebar'da görünür. Sayfa içeride flag'i tekrar
      // kontrol eder; doğrudan URL ile gelinirse "kapalı" uyarısı.
      { path: "venues/:venueId/yemeksepeti", element: <YemeksepetiPage /> },

      // Trendyol GO — beta entegrasyon, sadece admin tarafından açılan
      // venue'lerde sidebar'da görünür. Sayfa içeride flag'i tekrar kontrol eder.
      { path: "venues/:venueId/trendyolgo", element: <TrendyolgoPage /> },

      // ─── Eski URL redirect'leri (geri uyum) ───
      // VenueRedirect helper absolute yola gönderir → `..` çözümü
      // route hierarchy yerine doğru `/venues/:id/X` üretir.
      { path: "venues/:venueId/menus", element: <VenueRedirect to="menu" /> },
      { path: "venues/:venueId/theme", element: <VenueRedirect to="menu/themes" /> },
      { path: "venues/:venueId/announcement", element: <VenueRedirect to="menu/announcement" /> },
      { path: "venues/:venueId/banners", element: <VenueRedirect to="menu/banners" /> },
      { path: "venues/:venueId/tables", element: <VenueRedirect to="orders" /> },
      { path: "venues/:venueId/kitchen", element: <VenueRedirect to="orders/kitchen" /> },
      { path: "venues/:venueId/staff", element: <VenueRedirect to="orders/staff" /> },
      { path: "venues/:venueId/reviews", element: <VenueRedirect to="customers" /> },
      { path: "venues/:venueId/loyalty", element: <VenueRedirect to="customers/loyalty" /> },
      { path: "venues/:venueId/analytics", element: <VenueRedirect to="reports" /> },
      { path: "venues/:venueId/sales", element: <VenueRedirect to="reports/sales" /> },
      { path: "venues/:venueId/logs", element: <VenueRedirect to="reports/logs" /> },

      { path: "settings", element: <SettingsPage /> },
    ],
  },
]);
